import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { MapPin, Monitor, ShoppingCart, ArrowRight, ArrowLeft, CreditCard, CheckCircle, Download, Mail, BookOpen, Newspaper, Bell, Video, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { SEOHead } from '@/components/SEOHead';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/hooks/useAuth';
import { eligibleTrainingsData } from '@/data/eligibleTrainings';
import { supabase } from '@/integrations/supabase/client';
import { z } from 'zod';
import { WaitlistDialog } from '@/components/training/WaitlistDialog';

type Step = 'modality' | 'webinars' | 'cart' | 'account' | 'payment' | 'confirmation';

const guestSchema = z.object({
  fullName: z.string().trim().min(2, 'Minimum 2 caractères').max(100),
  email: z.string().trim().email('Email invalide').max(255),
});

const WEBINAR_PRICE = 199.99;

const getWebinars = (programSections: { title: string }[], fr: boolean) => {
  const webinars = programSections.slice(0, 4).map((s, i) => ({
    index: i + 1,
    title: s.title,
  }));
  webinars.push({
    index: 5,
    title: fr ? 'Cas Pratiques & Intégration Cabinet' : 'Case Studies & Firm Integration',
  });
  return webinars;
};

export const EnrollmentPage = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const fr = language === 'fr';

  const trainingSlug = searchParams.get('training') || '';
  const catalog = fr ? eligibleTrainingsData.fr : eligibleTrainingsData.en;
  const training = (catalog as Record<string, typeof eligibleTrainingsData.fr[keyof typeof eligibleTrainingsData.fr]>)[trainingSlug];

  const [step, setStep] = useState<Step>('modality');
  const [modality, setModality] = useState<'onsite' | 'remote'>('onsite');
  const [selectedWebinars, setSelectedWebinars] = useState<number[]>([]);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestErrors, setGuestErrors] = useState<Record<string, string>>({});
  const [acceptCGU, setAcceptCGU] = useState(false);
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvc, setCardCvc] = useState('');
  const [showWaitlist, setShowWaitlist] = useState(false);

  useEffect(() => {
    if (step === 'payment') setShowWaitlist(true);
  }, [step]);

  if (!training) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <h1 className="text-2xl font-bold text-foreground">{fr ? 'Formation non trouvée' : 'Training not found'}</h1>
          <Button asChild>
            <Link to={fr ? '/formation-ia-juridique-eligible-financement' : '/legal-ai-training-eligible-funding'}>{fr ? 'Retour au catalogue' : 'Back to catalog'}</Link>
          </Button>
        </div>
      </div>
    );
  }

  const webinars = getWebinars(training.program, fr);

  const toggleWebinar = (index: number) => {
    setSelectedWebinars(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index].sort()
    );
  };

  const price = modality === 'onsite'
    ? training.priceOnsite
    : `${(selectedWebinars.length * WEBINAR_PRICE).toFixed(2).replace('.', ',')} € HT`;
  const duration = modality === 'onsite' ? training.durationOnsite : `${selectedWebinars.length * 2}h (${selectedWebinars.length} webinaire${selectedWebinars.length > 1 ? 's' : ''})`;

  const handleAccountStep = () => {
    if (user) {
      setStep('payment');
    } else {
      setStep('account');
    }
  };

  const handleGuestContinue = () => {
    setGuestErrors({});
    try {
      guestSchema.parse({ fullName: guestName, email: guestEmail });
      setStep('payment');
    } catch (err) {
      if (err instanceof z.ZodError) {
        const errors: Record<string, string> = {};
        err.errors.forEach(e => { if (e.path[0]) errors[e.path[0] as string] = e.message; });
        setGuestErrors(errors);
      }
    }
  };

  const handlePayment = async () => {
    // Save enrollment via validated server-side RPC function
    const { error } = await supabase.rpc('insert_enrollment', {
      p_training_slug: trainingSlug,
      p_training_title: training.title,
      p_modality: modality,
      p_total_price: price,
      p_selected_webinars: modality === 'remote' ? selectedWebinars.map(i => {
        const w = webinars.find(wb => wb.index === i);
        return { index: i, title: w?.title || '' };
      }) : null,
      p_guest_name: user ? null : guestName || null,
      p_guest_email: user ? null : guestEmail || null,
    });

    if (error) {
      console.error('Enrollment error:', error.message);
      return;
    }

    setStep('confirmation');
  };

  // Dynamic steps based on modality
  const allSteps: { key: Step; label: string }[] = modality === 'remote'
    ? [
        { key: 'modality', label: fr ? 'Modalité' : 'Modality' },
        { key: 'webinars', label: fr ? 'Webinaires' : 'Webinars' },
        { key: 'cart', label: fr ? 'Panier' : 'Cart' },
        { key: 'account', label: fr ? 'Compte' : 'Account' },
        { key: 'payment', label: fr ? 'Paiement' : 'Payment' },
        { key: 'confirmation', label: fr ? 'Confirmation' : 'Confirmation' },
      ]
    : [
        { key: 'modality', label: fr ? 'Modalité' : 'Modality' },
        { key: 'cart', label: fr ? 'Panier' : 'Cart' },
        { key: 'account', label: fr ? 'Compte' : 'Account' },
        { key: 'payment', label: fr ? 'Paiement' : 'Payment' },
        { key: 'confirmation', label: fr ? 'Confirmation' : 'Confirmation' },
      ];

  const currentStepIndex = allSteps.findIndex(s => s.key === step);

  return (
    <div className="min-h-screen bg-secondary/30">
      <SEOHead
        title={fr ? `Inscription - ${training.title} | Gaius` : `Enrollment - ${training.title} | Gaius`}
        description={fr ? `Inscrivez-vous à la formation ${training.title}` : `Enroll in ${training.title} training`}
      />

      <div className="max-w-3xl mx-auto px-4 py-8 sm:py-12">
        {/* Progress bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {allSteps.map((s, i) => (
              <div key={s.key} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  i < currentStepIndex ? 'bg-primary text-primary-foreground' :
                  i === currentStepIndex ? 'bg-primary text-primary-foreground ring-4 ring-primary/20' :
                  'bg-muted text-muted-foreground'
                }`}>
                  {i < currentStepIndex ? <CheckCircle className="w-4 h-4" /> : i + 1}
                </div>
                {i < allSteps.length - 1 && (
                  <div className={`hidden sm:block w-12 lg:w-16 h-0.5 mx-1 ${i < currentStepIndex ? 'bg-primary' : 'bg-muted'}`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between">
            {allSteps.map(s => (
              <span key={s.key} className="text-xs text-muted-foreground hidden sm:block">{s.label}</span>
            ))}
          </div>
        </div>

        {/* Step 1: Modality */}
        {step === 'modality' && (
          <Card className="border-border shadow-lg">
            <CardHeader>
              <CardTitle className="text-xl">{fr ? 'Choisissez votre modalité' : 'Choose your modality'}</CardTitle>
              <p className="text-sm text-muted-foreground">{training.title}</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <RadioGroup value={modality} onValueChange={(v) => { setModality(v as 'onsite' | 'remote'); setSelectedWebinars([]); }} className="space-y-3">
                <label className={`flex items-start gap-4 p-4 rounded-xl border cursor-pointer transition-all ${modality === 'onsite' ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border hover:border-primary/30'}`}>
                  <RadioGroupItem value="onsite" className="mt-1" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <MapPin className="w-4 h-4 text-primary" />
                      <span className="font-semibold text-foreground">{fr ? 'Présentiel' : 'On-site'}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{training.durationOnsite}</p>
                    <p className="text-lg font-bold text-foreground mt-1">{training.priceOnsite}</p>
                    
                  </div>
                </label>
                <label className={`flex items-start gap-4 p-4 rounded-xl border cursor-pointer transition-all ${modality === 'remote' ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border hover:border-primary/30'}`}>
                  <RadioGroupItem value="remote" className="mt-1" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Monitor className="w-4 h-4 text-primary" />
                      <span className="font-semibold text-foreground">{fr ? 'Distanciel — Webinaires' : 'Remote — Webinars'}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{fr ? '5 webinaires de 2h — sélection au choix' : '5 webinars of 2h — choose your selection'}</p>
                    <p className="text-lg font-bold text-foreground mt-1">{`${WEBINAR_PRICE.toFixed(2).replace('.', ',')} € HT / ${fr ? 'webinaire' : 'webinar'}`}</p>
                    <p className="text-xs text-muted-foreground">{fr ? 'Accès illimité 30 jours' : 'Unlimited access 30 days'}</p>
                  </div>
                </label>
              </RadioGroup>
              <div className="flex justify-between pt-4">
                <Button variant="outline" asChild>
                  <Link to={fr ? `/formations/eligibles/${trainingSlug}` : `/training/eligible/${trainingSlug}`}>
                    <ArrowLeft className="w-4 h-4 mr-2" />{fr ? 'Retour' : 'Back'}
                  </Link>
                </Button>
                <Button onClick={() => modality === 'remote' ? setStep('webinars') : setStep('cart')}>
                  {fr ? 'Continuer' : 'Continue'}<ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Webinar Selection (remote only) */}
        {step === 'webinars' && (
          <Card className="border-border shadow-lg">
            <CardHeader>
              <CardTitle className="text-xl flex items-center gap-2">
                <Video className="w-5 h-5 text-primary" />
                {fr ? 'Sélectionnez vos webinaires' : 'Select your webinars'}
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                {fr ? `Choisissez un ou plusieurs webinaires — ${WEBINAR_PRICE.toFixed(2).replace('.', ',')} € HT chacun` : `Choose one or more webinars — €${WEBINAR_PRICE.toFixed(2)} excl. VAT each`}
              </p>
            </CardHeader>
            <CardContent className="space-y-3">
              {webinars.map((w) => (
                <label
                  key={w.index}
                  className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-all ${
                    selectedWebinars.includes(w.index)
                      ? 'border-primary bg-primary/5 ring-1 ring-primary'
                      : 'border-border hover:border-primary/30'
                  }`}
                >
                  <Checkbox
                    checked={selectedWebinars.includes(w.index)}
                    onCheckedChange={() => toggleWebinar(w.index)}
                  />
                  <div className="flex-1">
                    <span className="font-semibold text-foreground">
                      {fr ? `Webinaire ${w.index}/5` : `Webinar ${w.index}/5`} : {w.title}
                    </span>
                    <p className="text-sm text-muted-foreground">{fr ? '2 heures' : '2 hours'}</p>
                  </div>
                  <span className="text-sm font-bold text-foreground">{`${WEBINAR_PRICE.toFixed(2).replace('.', ',')} €`}</span>
                </label>
              ))}

              {selectedWebinars.length > 0 && (
                <div className="border-t border-border pt-4 flex justify-between items-center">
                  <span className="text-muted-foreground">
                    {fr ? `${selectedWebinars.length} webinaire${selectedWebinars.length > 1 ? 's' : ''} sélectionné${selectedWebinars.length > 1 ? 's' : ''}` : `${selectedWebinars.length} webinar${selectedWebinars.length > 1 ? 's' : ''} selected`}
                  </span>
                  <span className="text-xl font-bold text-foreground">{price}</span>
                </div>
              )}

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setStep('modality')}>
                  <ArrowLeft className="w-4 h-4 mr-2" />{fr ? 'Retour' : 'Back'}
                </Button>
                <Button onClick={() => setStep('cart')} disabled={selectedWebinars.length === 0}>
                  {fr ? 'Continuer' : 'Continue'}<ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Cart */}
        {step === 'cart' && (
          <Card className="border-border shadow-lg">
            <CardHeader>
              <CardTitle className="text-xl flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-primary" />
                {fr ? 'Votre panier' : 'Your cart'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 rounded-xl border border-border bg-card">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="font-semibold text-foreground">{training.title}</h3>
                    <p className="text-sm text-muted-foreground">{training.subtitle}</p>
                  </div>
                </div>

                {modality === 'onsite' ? (
                  <div className="flex flex-wrap gap-3 text-sm text-muted-foreground mt-3">
                    <Badge variant="outline">{fr ? 'Présentiel' : 'On-site'}</Badge>
                    <span>{training.durationOnsite}</span>
                  </div>
                ) : (
                  <div className="mt-3 space-y-2">
                    <Badge variant="outline">{fr ? 'Distanciel' : 'Remote'}</Badge>
                    {selectedWebinars.map(idx => {
                      const w = webinars.find(wb => wb.index === idx);
                      return (
                        <div key={idx} className="flex justify-between text-sm pl-2 border-l-2 border-primary/30">
                          <span className="text-muted-foreground">{fr ? `Webinaire ${idx}/5` : `Webinar ${idx}/5`} : {w?.title}</span>
                          <span className="text-foreground font-medium">{`${WEBINAR_PRICE.toFixed(2).replace('.', ',')} €`}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="border-t border-border mt-4 pt-4 flex justify-between items-center">
                  <span className="text-muted-foreground">{fr ? 'Total HT' : 'Total excl. VAT'}</span>
                  <span className="text-2xl font-bold text-foreground">{price}</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Badge className="bg-primary/10 text-primary border-primary/20">OPCO</Badge>
                <Badge className="bg-primary/10 text-primary border-primary/20">FIF-PL</Badge>
                <Badge className="bg-primary/10 text-primary border-primary/20">CPF</Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                {fr ? 'Cette formation est éligible au financement OPCO, FIF-PL et CPF. Contactez-nous pour une prise en charge.' : 'This training is eligible for OPCO, FIF-PL and CPF funding. Contact us for coverage.'}
              </p>

              {/* CGU opt-in */}
              <div className="p-4 rounded-xl border border-border bg-muted/30">
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox checked={acceptCGU} onCheckedChange={(v) => setAcceptCGU(v === true)} className="mt-0.5" />
                  <span className="text-sm text-muted-foreground">
                    {fr ? (
                      <>J'accepte les <Link to="/conditions-utilisation" className="text-primary underline hover:no-underline">conditions générales d'utilisation</Link> et la <Link to="/confidentialite" className="text-primary underline hover:no-underline">politique de confidentialité</Link> de Gaius.</>
                    ) : (
                      <>I accept the <Link to="/terms-of-service" className="text-primary underline hover:no-underline">terms of service</Link> and <Link to="/privacy-policy" className="text-primary underline hover:no-underline">privacy policy</Link>.</>
                    )}
                  </span>
                </label>
              </div>

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => modality === 'remote' ? setStep('webinars') : setStep('modality')}>
                  <ArrowLeft className="w-4 h-4 mr-2" />{fr ? 'Retour' : 'Back'}
                </Button>
                <Button onClick={handleAccountStep} disabled={!acceptCGU}>
                  {fr ? 'Continuer' : 'Continue'}<ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Account */}
        {step === 'account' && (
          <Card className="border-border shadow-lg">
            <CardHeader>
              <CardTitle className="text-xl">{fr ? 'Votre compte' : 'Your account'}</CardTitle>
              <p className="text-sm text-muted-foreground">
                {fr ? 'Créez un compte pour suivre vos formations, ou continuez en tant qu\'invité.' : 'Create an account to track your trainings, or continue as guest.'}
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Button asChild variant="outline" className="h-auto p-4 flex flex-col items-start gap-2">
                  <Link to={fr ? '/connexion' : '/auth'}>
                    <span className="font-semibold">{fr ? 'Se connecter' : 'Log in'}</span>
                    <span className="text-xs text-muted-foreground">{fr ? 'J\'ai déjà un compte Gaius' : 'I already have a Gaius account'}</span>
                  </Link>
                </Button>
                <Button asChild variant="outline" className="h-auto p-4 flex flex-col items-start gap-2">
                  <Link to={fr ? '/connexion?tab=signup' : '/auth?tab=signup'}>
                    <span className="font-semibold">{fr ? 'Créer un compte' : 'Create account'}</span>
                    <span className="text-xs text-muted-foreground">{fr ? 'Accès espace Gaius et attestations' : 'Access your Gaius space and certificates'}</span>
                  </Link>
                </Button>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
                <div className="relative flex justify-center text-xs"><span className="bg-card px-2 text-muted-foreground">{fr ? 'ou' : 'or'}</span></div>
              </div>

              <div className="space-y-4">
                <h3 className="font-medium text-foreground">{fr ? 'Continuer en tant qu\'invité' : 'Continue as guest'}</h3>
                <div className="space-y-2">
                  <Label>{fr ? 'Nom complet' : 'Full name'}</Label>
                  <Input value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder="Jean Dupont" className={guestErrors.fullName ? 'border-destructive' : ''} />
                  {guestErrors.fullName && <p className="text-xs text-destructive">{guestErrors.fullName}</p>}
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} placeholder="jean@cabinet.fr" className={guestErrors.email ? 'border-destructive' : ''} />
                  {guestErrors.email && <p className="text-xs text-destructive">{guestErrors.email}</p>}
                </div>
                <Button onClick={handleGuestContinue} className="w-full">
                  {fr ? 'Continuer en invité' : 'Continue as guest'}<ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>

              <Button variant="ghost" onClick={() => setStep('cart')} className="w-full">
                <ArrowLeft className="w-4 h-4 mr-2" />{fr ? 'Retour au panier' : 'Back to cart'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 5: Payment */}
        {step === 'payment' && (
          <Card className="border-border shadow-lg">
            <CardHeader>
              <CardTitle className="text-xl flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-primary" />
                {fr ? 'Paiement par carte bancaire' : 'Card payment'}
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                {fr ? 'Paiement sécurisé — Intégration Stripe à venir' : 'Secure payment — Stripe integration coming soon'}
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 rounded-lg bg-accent/10 border border-accent/20 text-sm text-accent-foreground">
                {fr ? '⚠️ Mode simulation : aucun paiement réel ne sera effectué.' : '⚠️ Simulation mode: no real payment will be processed.'}
              </div>

              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>{fr ? 'Numéro de carte' : 'Card number'}</Label>
                  <Input placeholder="4242 4242 4242 4242" value={cardNumber} onChange={(e) => setCardNumber(e.target.value)} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>{fr ? 'Expiration' : 'Expiry'}</Label>
                    <Input placeholder="MM/AA" value={cardExpiry} onChange={(e) => setCardExpiry(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>CVC</Label>
                    <Input placeholder="123" value={cardCvc} onChange={(e) => setCardCvc(e.target.value)} />
                  </div>
                </div>
              </div>

              <div className="border-t border-border pt-4 flex justify-between items-center">
                <span className="text-muted-foreground">{fr ? 'Total à payer' : 'Total'}</span>
                <span className="text-2xl font-bold text-foreground">{price}</span>
              </div>

              <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={() => user ? setStep('cart') : setStep('account')}>
                  <ArrowLeft className="w-4 h-4 mr-2" />{fr ? 'Retour' : 'Back'}
                </Button>
                <Button onClick={handlePayment} size="lg">
                  {fr ? 'Payer' : 'Pay'} {price}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 6: Confirmation */}
        {step === 'confirmation' && (
          <div className="space-y-6">
            <Card className="border-border shadow-lg">
              <CardContent className="p-8 text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                  <CheckCircle className="w-8 h-8 text-primary" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">
                  {fr ? 'Inscription confirmée !' : 'Enrollment confirmed!'}
                </h2>
                <p className="text-muted-foreground max-w-md mx-auto">
                  {fr
                    ? 'Vous recevrez un email de confirmation avec les détails de votre formation.'
                    : 'You will receive a confirmation email with your training details.'}
                </p>

                <div className="bg-secondary/50 rounded-xl p-4 text-left space-y-2 max-w-sm mx-auto">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{fr ? 'Formation' : 'Training'}</span>
                    <span className="font-medium text-foreground">{training.title}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{fr ? 'Modalité' : 'Modality'}</span>
                    <span className="font-medium text-foreground">{modality === 'onsite' ? (fr ? 'Présentiel' : 'On-site') : (fr ? 'Distanciel' : 'Remote')}</span>
                  </div>
                  {modality === 'remote' && selectedWebinars.length > 0 && (
                    <div className="text-sm space-y-1">
                      <span className="text-muted-foreground">{fr ? 'Webinaires' : 'Webinars'}</span>
                      {selectedWebinars.map(idx => {
                        const w = webinars.find(wb => wb.index === idx);
                        return (
                          <div key={idx} className="pl-2 border-l-2 border-primary/30 text-foreground text-xs">
                            {fr ? `Webinaire ${idx}/5` : `Webinar ${idx}/5`} : {w?.title}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{fr ? 'Durée' : 'Duration'}</span>
                    <span className="font-medium text-foreground">{duration}</span>
                  </div>
                  <div className="flex justify-between text-sm border-t border-border pt-2">
                    <span className="text-muted-foreground">{fr ? 'Montant' : 'Amount'}</span>
                    <span className="font-bold text-foreground">{price}</span>
                  </div>
                </div>

                <Button variant="outline" className="flex items-center gap-2 mx-auto">
                  <Download className="w-4 h-4" />
                  {fr ? 'Télécharger le récapitulatif PDF' : 'Download PDF summary'}
                </Button>
              </CardContent>
            </Card>

            {/* Suggestions */}
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">
                {fr ? 'Poursuivez votre parcours' : 'Continue your journey'}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Card className="border-border hover:shadow-md transition-all">
                  <CardContent className="p-4 flex items-start gap-3">
                    <BookOpen className="w-5 h-5 text-primary mt-0.5" />
                    <div>
                      <h4 className="font-medium text-foreground text-sm">{fr ? 'Autres formations' : 'More trainings'}</h4>
                      <p className="text-xs text-muted-foreground">{fr ? 'Découvrez notre catalogue complet' : 'Browse our full catalog'}</p>
                      <Button asChild variant="link" className="p-0 h-auto text-xs mt-1">
                        <Link to={fr ? '/formations/eligibles' : '/training/eligible'}>{fr ? 'Voir le catalogue' : 'View catalog'} →</Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-border hover:shadow-md transition-all">
                  <CardContent className="p-4 flex items-start gap-3">
                    <Newspaper className="w-5 h-5 text-primary mt-0.5" />
                    <div>
                      <h4 className="font-medium text-foreground text-sm">{fr ? 'Articles & actualités' : 'Articles & news'}</h4>
                      <p className="text-xs text-muted-foreground">{fr ? 'Restez informé sur l\'IA juridique' : 'Stay informed on legal AI'}</p>
                      <Button asChild variant="link" className="p-0 h-auto text-xs mt-1">
                        <Link to={fr ? '/actualites' : '/blog'}>{fr ? 'Lire les articles' : 'Read articles'} →</Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-border hover:shadow-md transition-all">
                  <CardContent className="p-4 flex items-start gap-3">
                    <Bell className="w-5 h-5 text-primary mt-0.5" />
                    <div>
                      <h4 className="font-medium text-foreground text-sm">Newsletter</h4>
                      <p className="text-xs text-muted-foreground">{fr ? 'Recevez nos dernières actualités' : 'Get our latest updates'}</p>
                      <Button asChild variant="link" className="p-0 h-auto text-xs mt-1">
                        <Link to="/contact">{fr ? 'S\'inscrire' : 'Subscribe'} →</Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                {user && (
                  <Card className="border-border hover:shadow-md transition-all">
                    <CardContent className="p-4 flex items-start gap-3">
                      <Mail className="w-5 h-5 text-primary mt-0.5" />
                      <div>
                        <h4 className="font-medium text-foreground text-sm">{fr ? 'Votre espace Gaius' : 'Your Gaius Space'}</h4>
                        <p className="text-xs text-muted-foreground">{fr ? 'Accédez à vos formations et attestations' : 'Access your trainings and certificates'}</p>
                        <Button asChild variant="link" className="p-0 h-auto text-xs mt-1">
                          <Link to={fr ? '/espace-client' : '/dashboard'}>{fr ? 'Mon espace Gaius' : 'My Gaius space'} →</Link>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <WaitlistDialog
        open={showWaitlist}
        onOpenChange={setShowWaitlist}
        trainingSlug={trainingSlug}
        trainingTitle={training?.title || ''}
        initialEmail={user?.email || guestEmail || ''}
      />
    </div>
  );
};
